import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  CreditCardIcon,
  AwardIcon,
  BarChart3Icon,
  CheckCircleIcon,
  TargetIcon,
  Loader2,
} from 'lucide-react';

const fmt = (n) => (n == null || Number.isNaN(Number(n)) ? '—' : Number(n).toLocaleString());
const pct = (p) => (p == null || Number.isNaN(Number(p)) ? '—' : `${Number(p).toFixed(1)}%`);

const QUALITY = {
  rose: {
    bar: 'bg-gradient-to-r from-rose-400 to-rose-600 shadow-sm shadow-rose-500/30',
    track: 'bg-rose-100/90 ring-1 ring-rose-200/60',
    pill: 'bg-rose-100 text-rose-900 ring-1 ring-rose-200/80',
    value: 'text-rose-800',
  },
  amber: {
    bar: 'bg-gradient-to-r from-amber-400 to-orange-500 shadow-sm shadow-amber-500/25',
    track: 'bg-amber-100/90 ring-1 ring-amber-200/60',
    pill: 'bg-amber-100 text-amber-950 ring-1 ring-amber-200/80',
    value: 'text-amber-900',
  },
  emerald: {
    bar: 'bg-gradient-to-r from-emerald-400 to-teal-600 shadow-sm shadow-emerald-500/25',
    track: 'bg-emerald-100/90 ring-1 ring-emerald-200/60',
    pill: 'bg-emerald-100 text-emerald-950 ring-1 ring-emerald-200/80',
    value: 'text-emerald-900',
  },
};

function QualityBar({ label, count, percent, tone }) {
  const q = QUALITY[tone] || QUALITY.emerald;
  const w = Math.min(100, Math.max(0, Number(percent) || 0));
  return (
    <div className="rounded-xl border border-slate-200/60 bg-white/80 p-3 shadow-sm ring-1 ring-slate-900/5 backdrop-blur-sm">
      <div className="flex justify-between gap-3 text-xs">
        <span className={`inline-flex max-w-[65%] items-center rounded-lg px-2 py-0.5 font-semibold ${q.pill}`}>{label}</span>
        <span className={`shrink-0 font-bold tabular-nums ${q.value}`}>
          {fmt(count)} · {pct(percent)}
        </span>
      </div>
      <div className={`mt-2.5 h-2.5 overflow-hidden rounded-full ${q.track}`}>
        <div className={`h-full rounded-full transition-all duration-500 ease-out ${q.bar}`} style={{ width: `${w}%` }} />
      </div>
    </div>
  );
}

const VARIANTS = {
  indigo: {
    card: 'border-indigo-200/80 bg-gradient-to-br from-indigo-50 via-white to-violet-50/50 ring-indigo-500/10',
    glow: 'from-indigo-400/20',
    icon: 'bg-indigo-500/15 text-indigo-800 ring-indigo-400/30',
    label: 'text-indigo-950/80',
    val: 'text-indigo-950',
    sub: 'text-indigo-900/85',
  },
  violet: {
    card: 'border-violet-200/80 bg-gradient-to-br from-violet-50 via-white to-purple-50/40 ring-violet-500/10',
    glow: 'from-violet-400/20',
    icon: 'bg-violet-500/15 text-violet-800 ring-violet-400/30',
    label: 'text-violet-950/80',
    val: 'text-violet-950',
    sub: 'text-violet-900/85',
  },
  purple: {
    card: 'border-purple-200/80 bg-gradient-to-br from-purple-50 via-white to-fuchsia-50/40 ring-purple-500/10',
    glow: 'from-purple-400/20',
    icon: 'bg-purple-500/15 text-purple-900 ring-purple-400/30',
    label: 'text-purple-950/80',
    val: 'text-purple-950',
    sub: 'text-purple-900/85',
  },
  fuchsia: {
    card: 'border-fuchsia-200/80 bg-gradient-to-br from-fuchsia-50 via-white to-pink-50/35 ring-fuchsia-500/10',
    glow: 'from-fuchsia-400/20',
    icon: 'bg-fuchsia-500/15 text-fuchsia-900 ring-fuchsia-400/30',
    label: 'text-fuchsia-950/80',
    val: 'text-fuchsia-950',
    sub: 'text-fuchsia-900/85',
  },
};

function SummaryTile({ icon: Icon, label, value, sub, variant }) {
  const v = VARIANTS[variant] || VARIANTS.indigo;
  return (
    <div className={`relative overflow-hidden rounded-2xl border p-4 shadow-md ring-1 transition hover:-translate-y-0.5 hover:shadow-lg ${v.card}`}>
      <div className={`pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full bg-gradient-to-br ${v.glow} to-transparent opacity-80`} />
      <div className="relative">
        {Icon && (
          <span className={`mb-2 inline-flex h-10 w-10 items-center justify-center rounded-xl ring-1 ${v.icon}`}>
            <Icon className="h-5 w-5" />
          </span>
        )}
        <p className={`text-[11px] font-bold uppercase tracking-wide ${v.label}`}>{label}</p>
        <p className={`mt-1 text-2xl font-black tabular-nums tracking-tight ${v.val}`}>{value}</p>
        {sub && <p className={`mt-1 text-xs font-semibold ${v.sub}`}>{sub}</p>}
      </div>
    </div>
  );
}

function TypeTile({ label, sessions, averageSuccessPercent, variant }) {
  const { t } = useTranslation();
  const v = VARIANTS[variant] || VARIANTS.violet;
  return (
    <div className={`relative overflow-hidden rounded-2xl border p-4 shadow-md ring-1 transition hover:-translate-y-0.5 hover:shadow-lg ${v.card}`}>
      <div className={`pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full bg-gradient-to-br ${v.glow} to-transparent opacity-80`} />
      <div className="relative">
        <span className={`mb-2 inline-flex h-10 w-10 items-center justify-center rounded-xl ring-1 ${v.icon}`}>
          <CreditCardIcon className="h-5 w-5" />
        </span>
        <p className={`text-[11px] font-bold uppercase tracking-wide ${v.label}`}>{label}</p>
        <p className={`mt-1 text-2xl font-black tabular-nums tracking-tight ${v.val}`}>{fmt(sessions)}</p>
        {averageSuccessPercent != null && !Number.isNaN(Number(averageSuccessPercent)) && (
          <p className={`mt-1 text-xs font-semibold ${v.sub}`}>{t('dashboard.stats.avgSuccess', { p: pct(averageSuccessPercent) })}</p>
        )}
      </div>
    </div>
  );
}

/**
 * فلاش كارد — /education/student-dashboard/flash-cards
 */
const StudentDashboardFlashCards = ({ data, loading, isRTL }) => {
  const { t } = useTranslation();

  const fcm = data?.metrics;
  const period = data?.period;
  const sq = fcm?.sessionQualityBuckets;
  const periodLabel =
    period?.seasonYearLabel || (period?.start && period?.end ? `${period.start} → ${period.end}` : null);

  const typeEntries =
    fcm?.byFlashCardType && typeof fcm.byFlashCardType === 'object'
      ? Object.entries(fcm.byFlashCardType).sort(([a], [b]) => Number(a) - Number(b))
      : [];
  const typeCycle = ['indigo', 'violet', 'purple', 'fuchsia'];

  return (
    <section id="student-dashboard-flash-cards" className="scroll-mt-24 overflow-visible rounded-[1.35rem] border-[3px] border-slate-900/10 bg-white/95 shadow-[0_8px_0_0_rgba(15,23,42,0.06)]">
      <div className="overflow-hidden rounded-t-[1.2rem] border-b border-violet-200/50 bg-gradient-to-r from-violet-500/12 via-indigo-50/80 to-fuchsia-50/50 px-4 py-3 sm:px-5">
        <div className={`flex flex-wrap items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white shadow-lg shadow-violet-500/35 ring-2 ring-white/50">
            <CreditCardIcon className="h-5 w-5" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-black tracking-tight text-violet-950 sm:text-lg">{t('dashboard.stats.flashCardsStats')}</h2>
            {periodLabel && <p className="mt-0.5 truncate text-xs font-semibold text-violet-800/75">{periodLabel}</p>}
          </div>
        </div>
      </div>
      <div className="relative p-4 sm:p-5">
        {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-b-[1.2rem] bg-white/75 backdrop-blur-[2px]">
            <Loader2 className="h-10 w-10 animate-spin text-violet-600" strokeWidth={2.5} aria-hidden />
          </div>
        )}
        {fcm ? (
          <>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <SummaryTile
                icon={AwardIcon}
                label={t('users.students.stats.flashCardLevelShort')}
                value={fmt(fcm.studentLevelNumber)}
                variant="indigo"
              />
              <SummaryTile
                icon={BarChart3Icon}
                label={t('dashboard.stats.totalSessions')}
                value={fmt(fcm.totalSessions)}
                variant="violet"
              />
              <SummaryTile
                icon={CheckCircleIcon}
                label={t('dashboard.stats.completedSessions')}
                value={fmt(fcm.completedSessions)}
                sub={t('dashboard.stats.percentOfTotalSessions', { p: pct(fcm.completedPercentOfTotal) })}
                variant="purple"
              />
              <SummaryTile
                icon={TargetIcon}
                label={t('users.students.stats.flashCardsPrimaryScope')}
                value={fmt(fcm.primaryScopeTotalSessions)}
                variant="fuchsia"
              />
            </div>

            {typeEntries.length > 0 && (
              <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {typeEntries.map(([id, row], i) => (
                  <TypeTile
                    key={id}
                    label={t('dashboard.stats.flashCardTypeN', { n: id })}
                    sessions={row.sessions}
                    averageSuccessPercent={row.averageSuccessPercent}
                    variant={typeCycle[i % typeCycle.length]}
                  />
                ))}
              </div>
            )}

            {sq && (
              <div className="mt-8 space-y-3">
                <h3 className="inline-flex rounded-lg bg-violet-100/90 px-3 py-1 text-xs font-bold uppercase tracking-wide text-violet-950 ring-1 ring-violet-300/50">
                  {t('dashboard.stats.answerQualitySessions')}
                </h3>
                <QualityBar
                  label={t('users.students.stats.qualityWeakUnder50')}
                  count={sq.weakUnder50Percent?.count}
                  percent={sq.weakUnder50Percent?.percentOfSessions}
                  tone="rose"
                />
                <QualityBar
                  label={t('users.students.stats.qualityMedium50to69')}
                  count={sq.medium50To69Percent?.count}
                  percent={sq.medium50To69Percent?.percentOfSessions}
                  tone="amber"
                />
                <QualityBar
                  label={t('users.students.stats.qualityGood70Plus')}
                  count={sq.good70PlusPercent?.count}
                  percent={sq.good70PlusPercent?.percentOfSessions}
                  tone="emerald"
                />
              </div>
            )}
          </>
        ) : (
          !loading && <p className="text-sm font-semibold text-slate-500">{t('users.students.stats.noData')}</p>
        )}
      </div>
    </section>
  );
};

export default StudentDashboardFlashCards;
